import React from 'react';
import { Table, ScrollArea, useMantineTheme, Text, Group } from '@mantine/core';
import { IconPencil, IconTrash, IconTag, IconBoxOff, IconTool } from '@tabler/icons-react';

import { utils } from "../../utils/globalVariables";
import { numberFormatter, sqlDateToHumanDate, getObjectParam2, safeValue, stringDivider } from "../../utils/globalFunctions";

import THSort from "../../components/general/THSort";
import THPlain from "../../components/general/THPlain";
import ActionIconComponent from "../../components/general/ActionIconComponent";

const StockItemTable = ({
  stateData,
  stateSortList,
  stateSortSelected,
  fnSort,
  stateDataOffset,
  fnSelectedDataEdit,
  fnSelectedDataDelete,
  fnSelectedDataView,
  fnSelectedDataTagging,
  fnSelectedInStock,
  fnSelectedKlaimGaransi
}) => {

  const theme = useMantineTheme();

  const TD = ({ children, stateNoWrap = true }) => {
    return (
      <td
        style={{
          whiteSpace: stateNoWrap ? 'nowrap' : 'normal',
          color: theme.colorScheme === 'light' ? theme.colors.gray[7] : theme.colors.gray[6],
        }}
      >
        {children}
      </td>
    )
  }

  return (
    <ScrollArea>
      <Table
        highlightOnHover
        verticalSpacing="sm"

        sx={() => ({
          backgroundColor: theme.colorScheme === 'light' ? '#fff' : theme.colors.dark[8],

          '& thead.tableHeadSticky': {
            position: 'sticky',
            top: '0px',
          },

          '& thead tr th': {
            paddingTop: theme.spacing.sm,
            paddingBottom: theme.spacing.sm,
          },
          '& thead tr th.THSort:hover': {
            backgroundColor: `${theme.colorScheme === 'light' ? theme.colors.gray[3] : theme.colors.dark[5]} !important`
          },
          '& thead tr th:not(:nth-last-of-type(1)) .wrapperContentTH': {
            borderRight: `1px solid ${theme.colorScheme === 'light' ? theme.colors.gray[4] : theme.colors.dark[4]}`,
          },
        })}
      >
        <thead>
          <tr>
            <THPlain stateCaption='No' />

            <THSort
              stateSortInUse={stateSortList.stockCategory}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THSort
              stateSortInUse={stateSortList.stockCategorySub}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THPlain stateCaption='Serial Number' />

            <THSort
              stateSortInUse={stateSortList.stockSupplier}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THSort
              stateSortInUse={stateSortList.stockTanggalBeli}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THSort
              stateSortInUse={stateSortList.stockHargaBeli}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THSort
              stateSortInUse={stateSortList.stockHargaJual}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THSort
              stateSortInUse={stateSortList.stockMasaGaransiType}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THSort
              stateSortInUse={stateSortList.stockMasaGaransi}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THSort
              stateSortInUse={stateSortList.stockJumlah}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THSort
              stateSortInUse={stateSortList.stockTanda}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THSort
              stateSortInUse={stateSortList.stockIsActive}
              stateSortSelected={stateSortSelected}
              fnSort={fnSort}
            />

            <THPlain stateCaption='' />
          </tr>
        </thead>

        <tbody>
          {Object.keys(stateData).map((key, index) => {
            return (
              <tr
                key={key}
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  fnSelectedDataView(stateData[key]);
                }}
              >
                <TD>{Number(stateDataOffset) + Number(index) + 1}</TD>

                <TD stateNoWrap={false}>
                  <Text lineClamp={2}>{stateData[key].stock_category_name}</Text>
                </TD>

                <TD stateNoWrap={false}>
                  <Text lineClamp={2}>{stateData[key].stock_category_sub_name}</Text>
                </TD>

                <TD stateNoWrap={true}>
                  <Text
                    sx={{
                      wordBreak: 'break-word'
                    }}
                  >
                    {stateData[key].stock_serial_number ? stateData[key].stock_serial_number : ' - '}
                  </Text>
                </TD>

                <TD stateNoWrap={false}>
                  <Text lineClamp={2}>{stateData[key].supplier_name}</Text>
                </TD>

                <TD stateNoWrap={true}>
                  {sqlDateToHumanDate({
                    stateDate: stateData[key].stock_tanggal_beli,
                    stateWithTime: false,
                    stateLang: 'id',
                    stateIsShort: false,
                    stateDay: false,
                    stateDefaultValue: ' - ',
                    stateDateType: 'day'
                  })}
                </TD>

                <TD stateNoWrap={true}>
                  {'Rp ' + numberFormatter({ stateValue: stateData[key].stock_harga_beli })}
                </TD>

                <TD stateNoWrap={true}>
                  {'Rp ' + numberFormatter({ stateValue: stateData[key].stock_harga_jual })}
                </TD>

                <TD stateNoWrap={true}>
                  {getObjectParam2(utils.masaGaransi, stateData[key].stock_masa_garansi_type, 'caption', '-')}
                </TD>

                <TD stateNoWrap={true}>
                  {safeValue(stateData[key].stock_masa_garansi_bulan, '0') + ' Bulan'} {safeValue(stateData[key].stock_masa_garansi_hari, '0') + ' Hari'}
                </TD>

                <TD>{numberFormatter({ stateValue: stateData[key].stock_jumlah })}</TD>

                <TD>
                  <Text
                    size='xs'
                  >
                    {getObjectParam2(utils.stockItemStatus, stateData[key].stock_status, 'caption', ' - ')}
                  </Text>

                  {stateData[key].customer_name &&
                    <Text
                      size='xs'
                      color={getObjectParam2(utils.stockItemStatus, stateData[key].stock_status, 'color', 'gray')}
                      mt='sm'
                    >
                      Tagged {safeValue(stateData[key].customer_name, ' - ')}
                    </Text>
                  }

                  {stateData[key].tagged_project_id &&
                    <Text
                      size='xs'
                      color={getObjectParam2(utils.stockItemStatus, stateData[key].stock_status, 'color', 'gray')}
                      mt='sm'
                    >
                      Tagged {stringDivider(stateData[key].tagged_project_id, [4, 6, 8], '.', ' - ')}
                    </Text>
                  }
                </TD>

                <TD>{stateData[key].stock_is_active === '1' ? 'Aktif' : 'Non Aktif'}</TD>

                <TD>
                  <Group spacing='xs' noWrap>
                    <ActionIconComponent
                      stateVariant='subtle'
                      stateRadius='xl'
                      stateTitle='Tagging'
                      stateSize='lg'

                      StateIcon={IconTag}
                      stateIconSize='1.1rem'
                      stateIconStrokeWidth={1}

                      stateSXActionIcon={{
                        '&:hover': {
                          backgroundColor: theme.colorScheme === 'light' ? theme.colors.gray[3] : theme.colors.dark[4]
                        },
                      }}

                      stateIconStyle={{
                        color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                      }}

                      fnOnClick={(e) => {
                        e.stopPropagation();
                        fnSelectedDataTagging(stateData[key]);
                      }}
                    />

                    <ActionIconComponent
                      stateVariant='subtle'
                      stateRadius='xl'
                      stateTitle='Stok Rusak'
                      stateSize='lg'

                      StateIcon={IconBoxOff}
                      stateIconSize='1.1rem'
                      stateIconStrokeWidth={1}

                      stateSXActionIcon={{
                        '&:hover': {
                          backgroundColor: theme.colorScheme === 'light' ? theme.colors.gray[3] : theme.colors.dark[4]
                        },
                      }}

                      stateIconStyle={{
                        color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                      }}

                      fnOnClick={(e) => {
                        e.stopPropagation();
                        fnSelectedInStock(stateData[key]);
                      }}
                    />

                    <ActionIconComponent
                      stateVariant='subtle'
                      stateRadius='xl'
                      stateTitle='Klaim Garansi'
                      stateSize='lg'

                      StateIcon={IconTool}
                      stateIconSize='1.1rem'
                      stateIconStrokeWidth={1}

                      stateSXActionIcon={{
                        '&:hover': {
                          backgroundColor: theme.colorScheme === 'light' ? theme.colors.gray[3] : theme.colors.dark[4]
                        },
                      }}

                      stateIconStyle={{
                        color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                      }}

                      fnOnClick={(e) => {
                        e.stopPropagation();
                        fnSelectedKlaimGaransi(stateData[key]);
                      }}
                    />

                    <ActionIconComponent
                      stateVariant='subtle'
                      stateRadius='xl'
                      stateTitle='Edit'
                      stateSize='lg'

                      StateIcon={IconPencil}
                      stateIconSize='1.1rem'
                      stateIconStrokeWidth={1}

                      stateSXActionIcon={{
                        '&:hover': {
                          backgroundColor: theme.colorScheme === 'light' ? theme.colors.gray[3] : theme.colors.dark[4]
                        },
                      }}

                      stateIconStyle={{
                        color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                      }}

                      fnOnClick={(e) => {
                        e.stopPropagation();
                        fnSelectedDataEdit(stateData[key]);
                      }}
                    />

                    <ActionIconComponent
                      stateVariant='subtle'
                      stateRadius='xl'
                      stateTitle='Delete'
                      stateSize='lg'

                      StateIcon={IconTrash}
                      stateIconSize='1.1rem'
                      stateIconStrokeWidth={1}

                      stateSXActionIcon={{
                        '&:hover': {
                          backgroundColor: theme.colorScheme === 'light' ? theme.colors.gray[3] : theme.colors.dark[4]
                        },
                      }}

                      stateIconStyle={{
                        color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0]
                      }}

                      fnOnClick={(e) => {
                        e.stopPropagation();
                        fnSelectedDataDelete(stateData[key]);
                      }}
                    />
                  </Group>
                </TD>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </ScrollArea>
  )
}

export default React.memo(StockItemTable);